 
import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common'; // Import CommonModule
import { Router, RouterModule, NavigationEnd } from '@angular/router';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatSnackBar } from '@angular/material/snack-bar'; // Import MatSnackBar
import { AuthService } from './services/auth/auth.service';
import { filter, Subject, takeUntil, debounceTime } from 'rxjs';
import { HeaderToolbarComponent } from './views/header-toolbar/header-toolbar.component';
import { MainLayoutComponent } from './views/main-layout/main-layout.component';
import { HeaderComponent } from './login/header/header.component';
import { LoadingSpinnerComponent } from './common/components/loading-spinner';
import { ActivityTrackingService } from './services/client/activity-tracking.service';
import { WorkspacesApiService } from './services/api/workspaces-api.service';
import { EventBusService, EventTypes } from './services/client/event-bus.service';
import { Workspace } from '@app-libs/model';
import { ConfigurationClientService } from './services/client/configuration-client.service';

@Component({
  selector: 'app-root',
  imports: [
    RouterModule,
    HeaderToolbarComponent,
    MainLayoutComponent,
    CommonModule,
    MatTooltipModule,
    LoadingSpinnerComponent,
    HeaderComponent,
  ],
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
})
export class AppComponent implements OnInit, OnDestroy{
  title = 'StudyBru';
  loading = true;
  isAuth = false;
  userEmail: string | null = 'Anonymous';
  workspaceName: string | null = '';
  workspace: Workspace | null = null;
  chatMenuTitle: string | undefined = undefined;

  showSearch = false;
  showNotifications = false;
  showPrompts = true;
  showAnalytics = true;

  // Observable for component lifecycle management
  private destroy$: Subject<void> = new Subject<void>();
  
  // Subject for debouncing workspace updates
  private workspaceUpdateRequest$ = new Subject<void>();

  // Get main layout we need for toggling menu
  @ViewChild(MainLayoutComponent) mainLayout!: MainLayoutComponent;

  constructor(
    private snackBar: MatSnackBar,
    private authService: AuthService,
    private activityService: ActivityTrackingService,
    private router: Router,
    private workspaceService: WorkspacesApiService,
    private eventBus: EventBusService,
    private configService: ConfigurationClientService,
  ) {
    if (!this.chatMenuTitle) {
      this.chatMenuTitle = this.configService.getSetting('chat', 'defaultChatTitle');
    }
  }

  async ngOnInit(): Promise<void> {
    // Set up debounced workspace update handling
    this.workspaceUpdateRequest$
      .pipe(
        debounceTime(300), // Debounce for 300ms to prevent rapid duplicate calls
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        this.performWorkspaceUpdate();
      });
    
    // Wait for intitial auth check to reduce UI flickering
    await this.authService.waitForInitialAuthCheck();

    // Add a delay of 2 seconds
    // await this.delay(2000);

    // Now set up the auth state subscription
    this.updateLodingText('Authenticating...');
    this.authService.isAuthenticatedWait()
      .pipe(takeUntil(this.destroy$))
      .subscribe((isAuth) => {
      this.isAuth = isAuth;
      const currentUrl = this.router.url;

      if (!isAuth && 
          currentUrl !== '/signup' && 
          currentUrl !== '/signout' &&
          currentUrl !== '/thank-you' && 
          currentUrl !== '/forgot-password' && 
          currentUrl !== '/reset-password') {

        console.log('User not logged in, redirecting to login page');
        this.router.navigate(['/login']);

      } else if (isAuth) {
        this.userEmail = this.authService.getCurrentUserEmail();
        this.updateCurrentWorkspace();

        // this.updateLodingText(`${this.userEmail}`);
        // console.log(`User logged in: ${this.userEmail}`);
      }

      this.hideLoader();
      this.loading = false; // Set loading to false after auth check
    });

    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        takeUntil(this.destroy$)
      )
      .subscribe((event: NavigationEnd) => {
        this.runOnRouteChange(event); // Call your function
      });

    this.subscribeToEvent(
      EventTypes.WORKSPACE_UPDATED,
      this.updateCurrentWorkspace.bind(this),
      this.destroy$
    );
  }

  updateCurrentWorkspace(): void {
    console.log('Updating current workspace...');
    this.workspaceUpdateRequest$.next();
  }
  
  private performWorkspaceUpdate(): void {
    this.workspaceService.getCurrentWorkspace()
      .pipe(takeUntil(this.destroy$))
      .subscribe((workspace: Workspace) => {
      if (workspace) {
        console.log('Current workspace:', workspace);
        if (workspace.properties && typeof workspace.properties === 'object') {
          const props = workspace.properties as { [key: string]: any };
          const label = props['chat_menu_label'];
          this.chatMenuTitle = label || this.configService.getSetting('chat', 'defaultChatTitle');
          console.log(label);
        }

        this.workspaceName = workspace.name || 'Default Workspace';
      }
    });
  }

  runOnRouteChange(event: NavigationEnd): void {
    // Add logic that should run on every route change
    const url = event.urlAfterRedirects;

    const user_id = this.authService.getCurrentUserId();
    if (!user_id) {
      return;
    }

    // Example: Trigger analytics tracking
    this.activityService.trackPageView(
      user_id,
      url,
      event.url !== url ? event.url : undefined
    );
  }

  onToggleSidenav() {
    this.mainLayout.sidenav.toggle();
  }

  hideLoader() {
    const loader = document.getElementById('app-loader');
    if (loader) {
      loader.style.opacity = '0';
      loader.style.transition = 'opacity 0.5s ease-out';

      // Remove from DOM after fade-out
      setTimeout(() => {
        loader.remove();
      }, 500);
    }
  }

  updateLodingText(text: string) {
    const loader = document.getElementById('my-loading-text');
    if (loader) {
      loader.textContent = text;
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private subscribeToEvent(
    eventType: EventTypes,
    handler: (data: any) => void,
    destroy$: Subject<void>
  ): void {
    this.eventBus
      .on(eventType)
      .pipe(takeUntil(destroy$))
      .subscribe((event) => handler(event.data));
  }
}
